import { useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ChevronDown, List } from 'lucide-react';

const TocAccordion = styled.details`
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius.xl};
  background-color: ${(props) => props.theme.colors.card};
  margin: 0 0 1.75rem;
  overflow: hidden;

  &[open] summary svg.chevron {
    transform: rotate(180deg);
  }
`;

const TocSummary = styled.summary`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  cursor: pointer;
  list-style: none;
  padding: 0.875rem 1rem;
  font-weight: ${(props) => props.theme.typography.fontWeights.semibold};
  font-size: 0.9375rem;
  color: ${(props) => props.theme.colors.foreground};
  user-select: none;

  &::-webkit-details-marker {
    display: none;
  }

  @media (min-width: 768px) {
    padding: 1rem 1.25rem;
    font-size: 1rem;
  }
`;

const SummaryLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 1.125rem;
    height: 1.125rem;
    color: ${(props) => props.theme.colors.mutedForeground};
    flex-shrink: 0;
  }
`;

const Chevron = styled(ChevronDown)`
  width: 1.125rem;
  height: 1.125rem;
  color: ${(props) => props.theme.colors.mutedForeground};
  flex-shrink: 0;
  transition: transform 0.2s ease;
`;

const TocBody = styled.div`
  padding: 0 1rem 1rem;
  border-top: 1px solid ${(props) => props.theme.colors.border};

  @media (min-width: 768px) {
    padding: 0 1.25rem 1.25rem;
  }
`;

const ItemList = styled.ul`
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.125rem 1.25rem;

  @media (min-width: 520px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Item = styled.li`
  min-width: 0;
  padding-left: ${(props) => (props.$level === 3 ? '0.625rem' : '0')};
`;

const ItemLink = styled.a`
  display: block;
  padding: 0.3rem 0.4rem;
  border-radius: ${(props) => props.theme.borderRadius.md};
  font-size: ${(props) => (props.$level === 3 ? '0.8125rem' : '0.875rem')};
  line-height: 1.35;
  color: ${(props) =>
    props.$level === 3
      ? props.theme.colors.mutedForeground
      : props.theme.colors.foreground};
  text-decoration: none;
  transition: color 0.15s ease, background-color 0.15s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: ${(props) => props.theme.colors.primary};
    background-color: ${(props) => props.theme.colors.muted};
    white-space: normal;
  }
`;

function TocLinks({ items, onNavigate }) {
  return (
    <ItemList>
      {items.map((item) => (
        <Item key={item.id} $level={item.level}>
          <ItemLink
            href={`#${item.id}`}
            $level={item.level}
            onClick={(e) => onNavigate(e, item.id)}
            title={item.text}
          >
            {item.text}
          </ItemLink>
        </Item>
      ))}
    </ItemList>
  );
}

export default function TableOfContents({ items = [] }) {
  const { t } = useTranslation();

  const handleNavigate = useCallback((e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (window.history?.replaceState) {
      window.history.replaceState(null, '', `#${id}`);
    }
  }, []);

  if (!items.length) return null;

  return (
    <TocAccordion aria-label={t('article.tableOfContents', 'Table of contents')}>
      <TocSummary>
        <SummaryLabel>
          <List aria-hidden="true" />
          {t('article.tableOfContents', 'Table of contents')}
        </SummaryLabel>
        <Chevron className="chevron" aria-hidden="true" />
      </TocSummary>
      <TocBody>
        <TocLinks items={items} onNavigate={handleNavigate} />
      </TocBody>
    </TocAccordion>
  );
}
